import { useCallback, useState } from 'react'

import { Modal, TextInput, MultiSelect, Group } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import Button from 'src/components/Buttons/Button/Button'
import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_DOCUMENT_TEMPLATE_PACKAGES,
  SIGN_MULTIPLE_PACKAGES,
} from 'src/graphql/documentTemplatePackages.graphql'
import IconPlane from 'src/icons/IconPlane'

const onError = () => {
  toast('Something went wrong, please try again.', 'error')
}

const SendDocumentsToSignModal = ({ applicantId, email, isOpen, onClose }) => {
  const { currentUser } = useAuth()
  const [errorMessage, setErrorMessage] = useState(null)

  const { data, loading } = useQuery(GET_DOCUMENT_TEMPLATE_PACKAGES, {
    onError: (err) => {
      setErrorMessage(err)
      onError()
    },
  })

  const [signMultiplePackages, { loading: signMultiplePackagesLoading }] =
    useMutation(SIGN_MULTIPLE_PACKAGES, {
      onCompleted: (data) => {
        const results = data?.signMultiplePackages?.packages || []
        const successCount = results.filter((r) => r.success).length
        const failCount = results.filter((r) => !r.success).length
        if (failCount === 0) {
          toast('Documents sent successfully', 'success')
          onClose()
        } else if (successCount === 0) {
          toast('Failed to send document packages', 'error')
        } else {
          toast(
            `Sent ${successCount} packages, ${failCount} failed`,
            'warning'
          )
          onClose()
        }
      },
      onError: (error) => {
        setErrorMessage(error.message)
        onError()
      },
    })

  const packagesList =
    data?.documentTemplatePackages?.map((documentTemplatePackage) => ({
      value: documentTemplatePackage.documentTemplatePackageId,
      label: `${documentTemplatePackage.packageName}`,
      documentTemplatePackage,
    })) || []

  const schema = z.object({
    email: z.string().email({ message: 'Invalid email' }),
    companySignerEmail: z
      .string()
      .email({ message: 'Invalid email' })
      .optional()
      .or(z.literal('')),
    documentTemplatePackageIds: z
      .array(z.string())
      .min(1, 'Please select at least one package'),
  })

  const form = useForm({
    initialValues: {
      email,
      companySignerEmail: currentUser?.email ?? '',
      documentTemplatePackageIds: [] as string[],
    },
    validateInputOnBlur: true,
    validate: zodResolver(schema),
  })

  const handleSubmit = useCallback((values) => {
    signMultiplePackages({
      variables: {
        applicantEmail: values.email,
        companySignerEmail: values.companySignerEmail || undefined,
        documentTemplatePackageIds: values.documentTemplatePackageIds,
        applicantId,
      },
    })
  }, [])

  const handleError = useCallback((err) => {
    if (err.documentTemplatePackageIds || err.email || err.companySignerEmail) {
      setErrorMessage(err)
    } else {
      onError()
    }
  }, [])

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      classNames={{ title: 'text-lg font-bold' }}
      title="Send Documents for Signing"
      centered
    >
      <form
        onSubmit={form.onSubmit((values) => handleSubmit(values), handleError)}
      >
        <div className="flex flex-col gap-6">
          <TextInput
            placeholder={'Applicant Email'}
            required
            label="Applicant Email"
            classNames={{
              label: 'text-sm font-medium text-doubleNickel-gray-700',
            }}
            disabled={!packagesList?.length}
            {...form.getInputProps('email')}
          />
          <TextInput
            placeholder={'Company Signer Email'}
            label="Company Signer Email"
            description="Only applies to documents that require a company signature."
            classNames={{
              label: 'text-sm font-medium text-doubleNickel-gray-700',
            }}
            disabled={!packagesList?.length}
            {...form.getInputProps('companySignerEmail')}
          />
          <MultiSelect
            {...form.getInputProps('documentTemplatePackageIds')}
            label="Packages"
            placeholder="Select"
            withAsterisk={true}
            required
            searchable
            data={packagesList}
            disabled={!packagesList.length}
            error={!!errorMessage?.documentTemplatePackageIds}
            classNames={{
              label: 'text-sm font-medium text-doubleNickel-gray-700',
              input: 'text-sm text-doubleNickel-gray-700',
            }}
          />
          {!loading && !packagesList.length && (
            <StyledAlert
              variant="warning"
              description={
                <span data-testid="warning-message">
                  Currently, this functionality is not enabled.
                  <br />
                  Please contact the Double Nickel team to enable it and
                  accelerate your hiring process!
                </span>
              }
            />
          )}
        </div>

        <Group
          justify="flex-end"
          mt="md"
          className="grid grid-cols-2 gap-4 border-t py-4"
        >
          <Button text="Cancel" variant="outline" onClick={onClose} />
          <Button
            text="Send"
            variant="filled"
            type="submit"
            lefticon={<IconPlane />}
            loading={signMultiplePackagesLoading}
            disabled={!packagesList?.length}
          />
        </Group>
      </form>
    </Modal>
  )
}

export default SendDocumentsToSignModal
